/**
 * Utilitaires pour la validation et le traitement des IBAN/BIC
 */

// Interface pour stocker les informations complètes d'une banque française
interface FrenchBankInfo {
  name: string
  bic: string
}

// Table de mapping code banque → informations banque pour les banques françaises
// Source : specs/modules/accounting/referentiel_banques.csv
// Note: Certains codes banque sont partagés par plusieurs établissements (filiales)
// Dans ces cas, on ne peut pas distinguer les établissements par l'IBAN seul
const FRENCH_BANK_INFO: Record<string, FrenchBankInfo> = {
  '12240': { name: 'AXA Banque', bic: 'AXABFRPP' },
  '40978': { name: 'Banque Palatine', bic: 'BPALFRPP' },
  '14878': { name: 'Groupama Banque', bic: 'GPBAFRPP' },
  '30077': { name: 'Société Marseillaise de Crédit', bic: 'SMCTFRPP' },
  // Banques Populaires
  '17607': { name: 'BP Alsace Lorraine Champagne', bic: 'CCBPFRPPALC' },
  '10907': { name: 'BP Aquitaine Centre Atlantique', bic: 'CCBPFRPPBDX' },
  '16907': { name: 'BP Auvergne Rhône Alpes', bic: 'CCBPFRPPLYO' },
  '10807': { name: 'BP Bourgogne Franche-Comté', bic: 'CCBPFRPPDJN' },
  '13807': { name: 'BP Grand Ouest', bic: 'CCBPFRPPNAN' },
  '14607': { name: 'BP Méditerranée', bic: 'CCBPFRPPMAR' },
  '13507': { name: 'BP Nord', bic: 'CCBPFRPPNOR' },
  '17807': { name: 'BP Occitane', bic: 'CCBPFRPPTLS' },
  '10207': { name: 'BP Rives de Paris', bic: 'CCBPFRPPPRY' },
  '16607': { name: 'BP Sud', bic: 'CCBPFRPPMTP' },
  '18707': { name: 'BP Val de France', bic: 'CCBPFRPPVER' },
  '10107': { name: 'BRED Banque Populaire', bic: 'BREDFRPP' },
  '11307': { name: 'CASDEN Banque Populaire', bic: 'CEABORPP' },
  '42559': { name: 'Crédit Coopératif', bic: 'CABORPP' },
  // Banques nationales et d'investissement
  '31489': { name: 'Crédit Agricole CIB', bic: 'BSUIFRPP' },
  // Code 30006: partagé par BforBank et Crédit Agricole SA (même BIC)
  '30006': { name: 'Crédit Agricole / BforBank', bic: 'AGRIFRPP' },
  '40618': { name: 'Boursorama Banque', bic: 'BOUSFRPP' },
  '14518': { name: 'Fortuneo', bic: 'FTNOFRP1' },
  // Code 30004: partagé par Hello bank!, BNP Paribas, Nickel (même BIC)
  '30004': { name: 'BNP Paribas / Hello bank! / Nickel', bic: 'BNPAFRPP' },
  '14559': { name: 'ING France', bic: 'INGBFRPP' },
  '14690': { name: 'Monabanq', bic: 'MONAFRP1' },
  '16188': { name: 'BPCE', bic: 'BPCEFRPP' },
  '30066': { name: 'CIC', bic: 'CMCIFRPP' },
  '30076': { name: 'Crédit du Nord', bic: 'NORDFRPP' },
  '30056': { name: 'HSBC France', bic: 'CCFRFRPP' },
  '30002': { name: 'LCL (Crédit Lyonnais)', bic: 'CRLYFRPP' },
  '20041': { name: 'La Banque Postale', bic: 'PSSTFRPP' },
  '30007': { name: 'Natixis', bic: 'NATXFRPP' },
  '30003': { name: 'Société Générale', bic: 'SOGEFRPP' },
  // Banques privées et spécialisées
  '30788': { name: 'Banque Neuflize OBC', bic: 'NSMBFRPP' },
  '30568': { name: 'Banque Transatlantique', bic: 'BTRAFFPP' },
  '43199': { name: 'Crédit Foncier de France', bic: 'CFFRFRPP' },
  // CIC régionales
  '30087': { name: 'CIC Est', bic: 'CMCIFRPP' },
  // Code 30047: partagé par CIC Nord Ouest et CIC Ouest (même BIC)
  '30047': { name: 'CIC Nord Ouest / CIC Ouest', bic: 'CMCIFRPP' },
  '10057': { name: 'CIC Sud Ouest', bic: 'CMCIFRPP' },
  '10096': { name: 'Lyonnaise de Banque', bic: 'CMCIFRPP' },
  // Caisses d'Épargne
  '16705': { name: 'CE Alsace', bic: 'CEPAFRPP667' },
  '13335': { name: 'CE Aquitaine Poitou-Charentes', bic: 'CEPAFRPP333' },
  '18715': { name: 'CE Auvergne et Limousin', bic: 'CEPAFRPP871' },
  '12135': { name: 'CE Bourgogne Franche-Comté', bic: 'CEPAFRPP213' },
  '14445': { name: 'CE Bretagne Pays de Loire', bic: 'CEPAFRPP444' },
  '18315': { name: 'CE Côte d\'Azur', bic: 'CEPAFRPP831' },
  '15135': { name: 'CE Grand Est Europe', bic: 'CEPAFRPP513' },
  '16275': { name: 'CE Hauts de France', bic: 'CEPAFRPP627' },
  '17515': { name: 'CE Ile-de-France', bic: 'CEPAFRPP751' },
  '13485': { name: 'CE Languedoc Roussillon', bic: 'CEPAFRPP348' },
  '14265': { name: 'CE Loire Drôme Ardèche', bic: 'CEPAFRPP426' },
  '14505': { name: 'CE Loire-Centre', bic: 'CEPAFRPP450' },
  '13135': { name: 'CE Midi-Pyrénées', bic: 'CEPAFRPP313' },
  '11425': { name: 'CE Normandie', bic: 'CEPAFRPP142' },
  '11315': { name: 'CE Provence-Alpes-Corse', bic: 'CEPAFRPP131' },
  '13825': { name: 'CE Rhône Alpes', bic: 'CEPAFRPP382' },
  // Crédit Agricole régionales
  '11306': { name: 'CA Alpes Provence', bic: 'AGRIFRPP813' },
  '17206': { name: 'CA Alsace Vosges', bic: 'AGRIFRPP867' },
  '17906': { name: 'CA Anjou et Maine', bic: 'AGRIFRPP872' },
  '13306': { name: 'CA Aquitaine', bic: 'AGRIFRPP833' },
  '14706': { name: 'CA Atlantique Vendée', bic: 'AGRIFRPP844' },
  '18706': { name: 'CA Brie Picardie', bic: 'AGRIFRPP877' },
  '16806': { name: 'CA Centre France', bic: 'AGRIFRPP863' },
  '14806': { name: 'CA Centre Loire', bic: 'AGRIFRPP845' },
  '19506': { name: 'CA Centre Ouest', bic: 'AGRIFRPP879' },
  '17806': { name: 'CA Centre-Est', bic: 'AGRIFRPP871' },
  '11006': { name: 'CA Champagne-Bourgogne', bic: 'AGRIFRPP810' },
  '11706': { name: 'CA Charente-Maritime Deux-Sèvres', bic: 'AGRIFRPP817' },
  '12406': { name: 'CA Charente-Périgord', bic: 'AGRIFRPP824' },
  '12006': { name: 'CA Corse', bic: 'AGRIFRPP820' },
  '12206': { name: 'CA Côtes d\'Armor', bic: 'AGRIFRPP822' },
  '12906': { name: 'CA Finistère', bic: 'AGRIFRPP829' },
  '12506': { name: 'CA Franche-Comté', bic: 'AGRIFRPP825' },
  '14006': { name: 'CA Guadeloupe', bic: 'AGRIFRPP840' },
  '18206': { name: 'CA Ile-de-France', bic: 'AGRIFRPP882' },
  '13606': { name: 'CA Ille-et-Vilaine', bic: 'AGRIFRPP836' },
  '19906': { name: 'CA La Réunion', bic: 'AGRIFRPP899' },
  '13506': { name: 'CA Languedoc', bic: 'AGRIFRPP835' },
  '14506': { name: 'CA Loire Haute-Loire', bic: 'AGRIFRPP842' },
  '16106': { name: 'CA Lorraine', bic: 'AGRIFRPP857' },
  '19806': { name: 'CA Martinique Guyane', bic: 'AGRIFRPP891' },
  '16006': { name: 'CA Morbihan', bic: 'AGRIFRPP856' },
  '10206': { name: 'CA Nord Est', bic: 'AGRIFRPP802' },
  '11206': { name: 'CA Nord Midi-Pyrénées', bic: 'AGRIFRPP812' },
  '16706': { name: 'CA Nord de France', bic: 'AGRIFRPP859' },
  '16606': { name: 'CA Normandie', bic: 'AGRIFRPP866' },
  '18306': { name: 'CA Normandie-Seine', bic: 'AGRIFRPP876' },
  '19106': { name: 'CA Provence Côte d\'Azur', bic: 'AGRIFRPP883' },
  '16906': { name: 'CA Pyrénées Gascogne', bic: 'AGRIFRPP864' },
  '18106': { name: 'CA Savoie', bic: 'AGRIFRPP881' },
  '17106': { name: 'CA Sud Méditerranée', bic: 'AGRIFRPP866' },
  '13906': { name: 'CA Sud Rhône Alpes', bic: 'AGRIFRPP839' },
  '13106': { name: 'CA Toulouse 31', bic: 'AGRIFRPP831' },
  '19406': { name: 'CA Touraine et Poitou', bic: 'AGRIFRPP878' },
  '14406': { name: 'CA Val de France', bic: 'AGRIFRPP841' },
  // Crédit Mutuel
  '11808': { name: 'Banque Fédérative du Crédit Mutuel', bic: 'CMCIFR2A' },
  '10278': { name: 'CM Alliance Fédérale', bic: 'CMCIFR2A' },
  '15829': { name: 'CM Anjou', bic: 'CMCIFR2A' },
  '16159': { name: 'CM Antilles-Guyane', bic: 'CMCIFR2G' },
  '15589': { name: 'CM Arkéa', bic: 'CMBRFR2B' },
  '15459': { name: 'CM Centre', bic: 'CMCIFR2C' },
  '15749': { name: 'CM Loire-Atlantique Centre-Ouest', bic: 'CMCIFR2L' },
  '15489': { name: 'CM Maine-Anjou Basse-Normandie', bic: 'CMCIFR2M' },
  '15629': { name: 'CM Nord Europe', bic: 'CMCIFR2N' },
  '15959': { name: 'CM Normandie', bic: 'CMCIFR2N' },
  '15519': { name: 'CM Océan', bic: 'CMCIFR2O' },
  '15899': { name: 'CM Sud-Est', bic: 'CMCIFR2S' },
  // Crédit conso
  '18029': { name: 'BNP Paribas Personal Finance (Cetelem)', bic: 'CEABORPP' },
  '14940': { name: 'Cofidis', bic: 'COFIFRP1' },
  '14628': { name: 'Floa Bank', bic: 'FLBKFRPP' },
  '16760': { name: 'Franfinance', bic: 'FRAFFRPP' },
  '12869': { name: 'Oney Bank', bic: 'ONEYFRP1' },
  '41539': { name: 'Sofinco', bic: 'SOFRFRPP' },
  // Outre-mer
  '41839': { name: 'BDAF (Antilles Françaises)', bic: 'BDAFFRPP' },
  '18719': { name: 'BFCOI (Océan Indien)', bic: 'BFCOFRPP' },
  '14889': { name: 'Banque de Nouvelle-Calédonie', bic: 'BNCAFRPP' },
  '12149': { name: 'Banque de Polynésie', bic: 'BOPOLFRP' },
  '12239': { name: 'Banque de Tahiti', bic: 'BATAFRPP' },
  '12169': { name: 'Banque de la Réunion', bic: 'REUNFRPP' },
  // Établissements de paiement
  '16528': { name: 'Lemonway', bic: 'LEMYFRP1' },
  '16818': { name: 'Mangopay', bic: 'MPAYFRP1' },
  '16958': { name: 'Qonto', bic: 'QNTOFRP1' },
  '17418': { name: 'Shine', bic: 'SNNNFR22' },
  // Code 16798: partagé par Swile, Treezor, Pixpay (même BIC via Treezor)
  '16798': { name: 'Treezor / Swile / Pixpay', bic: 'TRZOFR21' },
}

// Configuration IBAN par pays européen
// Chaque pays a un format d'IBAN différent avec une position de code banque variable
interface CountryIbanConfig {
  length: number
  bankCodeStart: number
  bankCodeEnd: number
}

const COUNTRY_IBAN_CONFIG: Record<string, CountryIbanConfig> = {
  FR: { length: 27, bankCodeStart: 4, bankCodeEnd: 9 },    // France: 5 chiffres
  DE: { length: 22, bankCodeStart: 4, bankCodeEnd: 12 },   // Allemagne: 8 chiffres (BLZ)
  BE: { length: 16, bankCodeStart: 4, bankCodeEnd: 7 },    // Belgique: 3 chiffres
  NL: { length: 18, bankCodeStart: 4, bankCodeEnd: 8 },    // Pays-Bas: 4 lettres (code banque)
  ES: { length: 24, bankCodeStart: 4, bankCodeEnd: 8 },    // Espagne: 4 chiffres
  GB: { length: 22, bankCodeStart: 4, bankCodeEnd: 10 },   // UK: 6 chiffres (sort code)
  LT: { length: 20, bankCodeStart: 4, bankCodeEnd: 9 },    // Lituanie: 5 chiffres
}

// Table de mapping pour les banques européennes (hors France)
// Source : specs/modules/accounting/referentiel_banques.csv
const EUROPEAN_BANK_INFO: Record<string, { name: string; bic: string; country: string }> = {
  // Belgique
  'BE_23140': { name: 'Wise', bic: 'TRWIBEB1', country: 'BE' },
  // Allemagne
  'DE_10011': { name: 'N26', bic: 'NTSBDEB1', country: 'DE' },
  'DE_50040000': { name: 'Commerzbank', bic: 'COBADEFF', country: 'DE' },
  'DE_50070010': { name: 'Deutsche Bank', bic: 'DEUTDEFF', country: 'DE' },
  // Espagne
  'ES_0049': { name: 'Santander Espagne', bic: 'BSCHESMM', country: 'ES' },
  // Royaume-Uni
  'GB_203030': { name: 'Barclays UK', bic: 'BARCGB22', country: 'GB' },
  'GB_2140': { name: 'Revolut UK', bic: 'REVOGB21', country: 'GB' },
  // Lituanie
  'LT_20000': { name: 'Revolut LT', bic: 'REVOLT21', country: 'LT' },
  // Pays-Bas
  'NL_INGB': { name: 'ING Bank (Pays-Bas)', bic: 'INGBNL2A', country: 'NL' },
  'NL_BUNQ': { name: 'Bunq', bic: 'BUNQNL2A', country: 'NL' },
}

/**
 * Valide le format d'un IBAN
 */
export function isValidIban(iban: string): boolean {
  if (!iban) return false

  // Retirer les espaces
  const cleanIban = iban.replace(/\s/g, '')

  // Vérifier le format de base (2 lettres + 2 chiffres + max 30 caractères alphanumériques)
  if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]+$/.test(cleanIban)) {
    return false
  }

  // Vérifier la longueur (entre 15 et 34 caractères)
  if (cleanIban.length < 15 || cleanIban.length > 34) {
    return false
  }

  // Vérifier la clé de contrôle (algorithme modulo 97)
  const rearranged = cleanIban.slice(4) + cleanIban.slice(0, 4)
  const numericIban = rearranged
    .split('')
    .map((char) => {
      const code = char.charCodeAt(0)
      // A=10, B=11, ..., Z=35
      return code >= 65 ? code - 55 : char
    })
    .join('')

  // Calculer modulo 97
  let remainder = ''
  for (let i = 0; i < numericIban.length; i++) {
    remainder += numericIban[i]
    if (remainder.length >= 9) {
      remainder = (parseInt(remainder, 10) % 97).toString()
    }
  }

  return parseInt(remainder, 10) % 97 === 1
}

/**
 * Formate un IBAN en groupes de 4 caractères
 */
export function formatIban(iban: string): string {
  const cleanIban = iban.replace(/\s/g, '')
  return cleanIban.match(/.{1,4}/g)?.join(' ') || cleanIban
}

/**
 * Masque partiellement un IBAN pour l'affichage
 * Exemple: FR76 1234 5678 9012 3456 7890 123 → FR76 •••• •••• •••• 7890 123
 */
export function maskIban(iban: string): string {
  const cleanIban = iban.replace(/\s/g, '')

  if (cleanIban.length < 8) return iban

  const start = cleanIban.slice(0, 4) // 2 lettres pays + 2 chiffres clé
  const end = cleanIban.slice(-7) // 7 derniers caractères
  const middle = '•'.repeat(Math.min(cleanIban.length - 11, 16)) // Max 16 bullets

  return formatIban(start + middle + end)
}

/**
 * Déduit le BIC depuis un IBAN (français ou européen)
 */
export function deduceBicFromIban(iban: string): string | null {
  const cleanIban = iban.replace(/\s/g, '')
  const countryCode = cleanIban.substring(0, 2)

  // IBAN français
  if (countryCode === 'FR') {
    // IBAN français: FR76 10278 06099 00023456789 01
    //                     ^^^^^ code banque (positions 4-8)
    const bankCode = cleanIban.substring(4, 9)
    return FRENCH_BANK_INFO[bankCode]?.bic || null
  }

  // Autres pays européens
  const config = COUNTRY_IBAN_CONFIG[countryCode]
  if (!config) return null

  const bankCode = cleanIban.substring(config.bankCodeStart, config.bankCodeEnd)
  const key = `${countryCode}_${bankCode}`
  const bankInfo = EUROPEAN_BANK_INFO[key]

  return bankInfo?.bic || null
}

/**
 * Valide le format d'un BIC
 */
export function isValidBic(bic: string): boolean {
  if (!bic) return false

  // Format BIC: 8 ou 11 caractères (4 lettres + 2 lettres pays + 2 alphanumériques + optionnel 3 alphanumériques)
  return /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(bic.toUpperCase())
}

/**
 * Extrait le code banque d'un IBAN (français ou européen)
 */
export function extractBankCode(iban: string): string | null {
  const cleanIban = iban.replace(/\s/g, '')
  const countryCode = cleanIban.substring(0, 2)

  const config = COUNTRY_IBAN_CONFIG[countryCode]
  if (!config) return null
  if (cleanIban.length < config.bankCodeEnd) return null

  return cleanIban.substring(config.bankCodeStart, config.bankCodeEnd)
}

/**
 * Extrait le code pays d'un IBAN
 */
export function extractCountryCode(iban: string): string | null {
  const cleanIban = iban.replace(/\s/g, '')
  if (cleanIban.length < 2) return null
  return cleanIban.substring(0, 2)
}

/**
 * Retourne le nom de la banque si connue
 */
export function getBankName(iban: string): string | null {
  const cleanIban = iban.replace(/\s/g, '')
  const countryCode = extractCountryCode(cleanIban)
  const bankCode = extractBankCode(cleanIban)
  if (!countryCode || !bankCode) return null

  // Pour les banques françaises, utiliser FRENCH_BANK_INFO
  if (countryCode === 'FR') {
    return FRENCH_BANK_INFO[bankCode]?.name || null
  }

  // Pour les autres pays européens, utiliser EUROPEAN_BANK_INFO
  const key = `${countryCode}_${bankCode}`
  return EUROPEAN_BANK_INFO[key]?.name || null
}
